import { Signal } from 'framework/utils/Signal';
import { linear } from './math-utils';
import { Range } from './Range';

interface ILerperConfig {
  initValue: number;
  lerp: number;
  snap: number;
  autoUpdate: boolean;
  range: Range;
  destroySignal?: Signal<any>;
}

const defaultConfig: ILerperConfig = {
  initValue: 0,
  lerp: 0.5,
  snap: 0.01,
  autoUpdate: true,
  range: Range.getInfinityRange(),
}

export class Lerper {
  public signals = {
    onValueUpdate: new Signal<number>(),
    onUpdate: new Signal<[number, number]>(),
  }
  
  private readonly config: ILerperConfig;
  private currentValue: number;
  private targetValue: number;

  constructor(
    config: Partial<ILerperConfig> = {},
  ) {
    this.config = Object.assign({}, defaultConfig, config);

    const { initValue, autoUpdate, lerp, destroySignal, range } = this.config;

    this.currentValue = range.clamp(initValue);
    this.targetValue = this.currentValue;

    if (autoUpdate) {
      PIXI.ticker.shared.add(this.update);
    }

    if (destroySignal) destroySignal.addOnce(() => {
      if (autoUpdate) PIXI.ticker.shared.remove(this.update);
      this.signals.onUpdate.removeAll();
      this.signals.onValueUpdate.removeAll();
    });

    this.signals.onValueUpdate.memorize = true;
    this.signals.onValueUpdate.dispatch(this.currentValue);
  }

  public getTarget(): number {
    return this.targetValue;
  }

  public setTarget(value: number): void {
    this.targetValue = this.config.range.clamp(value);
  }

  public update = (): void => {
    this.signals.onValueUpdate.memorize = false;

    const { lerp, snap } = this.config;

    if (this.currentValue !== this.targetValue) {
      this.currentValue = linear(this.currentValue, this.targetValue, lerp);
      if (Math.abs(this.currentValue - this.targetValue) <= snap) this.currentValue = this.targetValue;
      this.signals.onValueUpdate.dispatch(this.currentValue);
    }

    this.signals.onUpdate.dispatch([this.currentValue, this.targetValue]);
  }
}
